'use client'
import * as React from 'react'
import { sidebarLocalStorageKeys } from '../lib/dom-keys'
import { useViewport } from './use-viewport'
import { create } from 'zustand'
import { useRenderDocumentSidebar } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/properties-panel/pp-layout'

export const useEditorWidth = create<undefined | number>(() => undefined)
export const useEditorHeight = create<undefined | number>(() => undefined)
export const useEditorMainContentHeight = create<undefined | number>(
  () => undefined
)

export const EditorWidthSetUp = () => {
  const [viewportWidth, viewportHeight] = useViewport()
  const viewportWidthRef = React.useRef(viewportWidth)
  const viewportHeightRef = React.useRef(viewportHeight)
  viewportWidthRef.current = viewportWidth
  viewportHeightRef.current = viewportHeight
  const [widthLeft, setWidthLeft] = React.useState<number>(260)
  const [widthRight, setWidthRight] = React.useState<number>(9)
  const [repoNavWidth, setRepoNavWidth] = React.useState<number>(73)
  const [headerHeight, setHeaderHeight] = React.useState<number>(0)

  const renderDocSidebar = useRenderDocumentSidebar()

  React.useLayoutEffect(() => {
    const leftPanel = document.querySelector<HTMLDivElement>(
      `#${sidebarLocalStorageKeys.repoSideBarWidth}`
    )
    const rightPanel = document.querySelector<HTMLDivElement>(
      `#${sidebarLocalStorageKeys.documentSidebarWidth}`
    )
    const navColumn = document.querySelector<HTMLDivElement>(
      `#${sidebarLocalStorageKeys.repoNavWidth}`
    )

    const repoNavWidth = navColumn?.clientWidth || 73
    setRepoNavWidth(repoNavWidth)

    let widthLeft = 260
    let widthRight = 9

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect
        const isLeft =
          entry.target.id === sidebarLocalStorageKeys.repoSideBarWidth

        if (isLeft) {
          widthLeft = width + 1
        } else {
          widthRight = width + 1
        }
      }

      setWidthLeft(widthLeft)
      setWidthRight(widthRight)

      const editorWidth = calculateEditorWidth({
        viewportWidth: viewportWidthRef.current,
        widthLeft,
        widthRight,
        repoNavWidth,
      })

      if (editorWidth !== null) {
        useEditorWidth.setState(editorWidth)
      }
    })

    if (leftPanel) {
      widthLeft = leftPanel.clientWidth + 1
      resizeObserver.observe(leftPanel)
    }

    if (rightPanel) {
      widthRight = rightPanel.clientWidth + 1
      resizeObserver.observe(rightPanel)
    }

    // set first time quickly to avoid flickering
    setWidthLeft(widthLeft)
    setWidthRight(widthRight)

    const editorWidth = calculateEditorWidth({
      viewportWidth: viewportWidthRef.current,
      widthLeft,
      widthRight,
      repoNavWidth,
    })

    if (editorWidth !== null) {
      useEditorWidth.setState(editorWidth)
    }

    const header = document.querySelector<HTMLDivElement>('header')
    const headerHeight = header?.clientHeight || 0
    setHeaderHeight(headerHeight)

    const editorHeight = calculateEditorHeight({
      viewportHeight: viewportHeightRef.current,
      headerHeight,
    })

    if (editorHeight !== null) {
      useEditorHeight.setState(editorHeight)
    }

    // Set up editor main content height from CSS variable
    const updateEditorMainContentHeight = () => {
      const editorMainContent = document.getElementById('editor-main-content')
      if (editorMainContent) {
        const computedStyle = getComputedStyle(editorMainContent)
        const editorHeightValue = computedStyle.getPropertyValue('--editor-height')
        if (editorHeightValue) {
          // Parse the CSS calc() value - we'll use a temporary element to compute the actual pixel value
          const tempDiv = document.createElement('div')
          tempDiv.style.position = 'absolute'
          tempDiv.style.visibility = 'hidden'
          tempDiv.style.height = editorHeightValue
          document.body.appendChild(tempDiv)
          const computedHeight = tempDiv.getBoundingClientRect().height
          document.body.removeChild(tempDiv)
          useEditorMainContentHeight.setState(computedHeight)
        }
      }
    }

    updateEditorMainContentHeight()

    // Use MutationObserver to watch for changes to the CSS variable
    const editorMainContent = document.getElementById('editor-main-content')
    let mutationObserver: MutationObserver | null = null
    
    if (editorMainContent) {
      mutationObserver = new MutationObserver(() => {
        updateEditorMainContentHeight()
      })
      mutationObserver.observe(editorMainContent, {
        attributes: true,
        attributeFilter: ['style']
      })
    }

    return () => {
      resizeObserver.disconnect()
      mutationObserver?.disconnect()
    }
  }, [renderDocSidebar])

  const editorWidth = calculateEditorWidth({
    viewportWidth,
    widthLeft,
    widthRight,
    repoNavWidth,
  })

  const editorHeight = calculateEditorHeight({
    viewportHeight,
    headerHeight,
  })

  React.useLayoutEffect(() => {
    if (editorWidth === null) return
    useEditorWidth.setState(editorWidth)
  }, [editorWidth])

  React.useLayoutEffect(() => {
    if (editorHeight === null) return
    useEditorHeight.setState(editorHeight)
  }, [editorHeight])

  // Update editor main content height when viewport changes
  React.useLayoutEffect(() => {
    const updateEditorMainContentHeight = () => {
      const editorMainContent = document.getElementById('editor-main-content')
      if (editorMainContent) {
        const computedStyle = getComputedStyle(editorMainContent)
        const editorHeightValue = computedStyle.getPropertyValue('--editor-height')
        if (editorHeightValue) {
          const tempDiv = document.createElement('div')
          tempDiv.style.position = 'absolute'
          tempDiv.style.visibility = 'hidden'
          tempDiv.style.height = editorHeightValue
          document.body.appendChild(tempDiv)
          const computedHeight = tempDiv.getBoundingClientRect().height
          document.body.removeChild(tempDiv)
          useEditorMainContentHeight.setState(computedHeight)
        }
      }
    }
    
    updateEditorMainContentHeight()
  }, [viewportWidth, viewportHeight])

  return null
}

function calculateEditorWidth({
  viewportWidth,
  widthLeft,
  widthRight,
  repoNavWidth,
}: {
  viewportWidth: number | undefined
  widthLeft: number
  widthRight: number
  repoNavWidth: number
}) {
  return viewportWidth
    ? viewportWidth - widthLeft - widthRight - repoNavWidth // sidebar icons width
    : null
}

function calculateEditorHeight({
  viewportHeight,
  headerHeight,
}: {
  viewportHeight: number | undefined
  headerHeight: number
}) {
  return viewportHeight ? viewportHeight - headerHeight : null
}
